import React, { useState, useEffect } from 'react';
import api from '../../api/axios';

const PaymentManagement = () => {
  const [payments, setPayments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterProject, setFilterProject] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creatingPayment, setCreatingPayment] = useState(false);

  const statusOptions = ['pending', 'Paid', 'Overdue', 'Cancelled'];
  const paymentTypes = ['Advance', 'General Payment', 'Closing Payment'];

  const [newPayment, setNewPayment] = useState({
    project_id: '',
    customer_id: '',
    amount: '',
    payment_type: '',
    due_date: '',
    description: '',
    invoice_number: '',
    status: 'pending'
  });

  useEffect(() => {
    fetchPayments();
    fetchProjects();
  }, []);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/admin/payments');
      setPayments(response.data.data);
    } catch (err) {
      console.error('Failed to fetch payments:', err);
      setError('Failed to load payments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await api.get('/admin/projects');
      setProjects(response.data.data);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    }
  };

  const handleCreatePayment = async (e) => {
    e.preventDefault();
    if (creatingPayment) return; // Prevent double submit
    setCreatingPayment(true);
    try {
      // Find the selected project to get the customer_id
      const selectedProject = projects.find(p => p.id === newPayment.project_id);
      if (!selectedProject) {
        alert('Please select a valid project');
        setCreatingPayment(false);
        return;
      }

      // Prepare payment data with proper field mapping
      const paymentData = {
        project_id: newPayment.project_id,
        customer_id: selectedProject.customer, // Get customer from selected project
        phase: newPayment.payment_type, // Map payment_type to phase
        amount: newPayment.amount,
        due_date: newPayment.due_date,
        description: newPayment.description,
        status: newPayment.status, // Keep the original case from frontend
        payment_method: 'bank-transfer', // Default payment method
        currency: 'INR' // Default currency
      };

      console.log('Sending payment data:', paymentData);

      const response = await api.post('/admin/payments', paymentData);
      fetchPayments();
      setShowAddModal(false);
      setNewPayment({
        project_id: '',
        customer_id: '',
        amount: '',
        payment_type: '',
        due_date: '',
        description: '',
        invoice_number: '',
        status: 'pending'
      });
    } catch (err) {
      console.error('Failed to create payment:', err);
      alert(`Failed to create payment: ${err.response?.data?.message || err.message}`);
    } finally {
      setCreatingPayment(false);
    }
  };

  const handleUpdatePaymentStatus = async (paymentId, status) => {
    try {
      const response = await api.put(`/admin/payments/${paymentId}`, { status });
      setPayments(prev => prev.map(p => p.id === paymentId ? response.data.data : p));
    } catch (err) {
      console.error('Failed to update payment status:', err);
      alert('Failed to update payment status. Please try again.');
    }
  };

  const generateInvoice = async (paymentId) => {
    try {
      const response = await api.post(`/admin/payments/${paymentId}/invoice`);
      // Handle invoice generation response
      alert('Invoice generated successfully!');
    } catch (err) {
      console.error('Failed to generate invoice:', err);
      alert('Failed to generate invoice. Please try again.');
    }
  };

  const getStatusColor = (status) => {
    const normalized = (status || '').toLowerCase();
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'paid': 'bg-green-100 text-green-800',
      'overdue': 'bg-red-100 text-red-800',
      'cancelled': 'bg-gray-100 text-gray-800'
    };
    return colors[normalized] || colors.pending;
  };

  const getPaymentTypeColor = (type) => {
    const colors = {
      'Advance': 'bg-blue-100 text-blue-800',
      'General Payment': 'bg-purple-100 text-purple-800',
      'Closing Payment': 'bg-green-100 text-green-800'
    };
    return colors[type] || colors.Other;
  };

  const isOverdue = (dueDate) => {
    return new Date(dueDate) < new Date() && new Date(dueDate).getTime() !== new Date('1970-01-01').getTime();
  };

  const filteredPayments = payments.filter(payment => {
    const matchesStatus = filterStatus === 'all' || payment.status.toLowerCase() === filterStatus.toLowerCase();
    const matchesProject =
      filterProject === 'all' ||
      payment.project === filterProject ||
      payment.project_id === filterProject ||
      (typeof payment.project === 'object' && payment.project?.id === filterProject);
    const matchesSearch = payment.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesProject && matchesSearch;
  });

  const totalAmount = filteredPayments
    .filter(payment => payment.status.toLowerCase() !== 'cancelled')
    .reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0);
  const pendingAmount = filteredPayments
    .filter(p => p.status.toLowerCase() === 'pending')
    .filter(p => p.status.toLowerCase() !== 'cancelled')
    .reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0);
  const overdueAmount = filteredPayments
    .filter(p => p.status.toLowerCase() === 'overdue')
    .filter(p => p.status.toLowerCase() !== 'cancelled')
    .reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0);
  const paidAmount = filteredPayments
    .filter(p => p.status.toLowerCase() === 'paid')
    .filter(p => p.status.toLowerCase() !== 'cancelled')
    .reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0);

  // Utility: Convert number to Indian format (1 lakh, 1 crore, etc.)
  function numberToIndianFormat(num) {
    if (num === 0) return '₹0';
    
    if (num >= 10000000) {
      const crore = Math.floor(num / 10000000);
      const remaining = num % 10000000;
      if (remaining === 0) {
        return `₹${crore} crore`;
      } else {
        const lakh = Math.floor(remaining / 100000);
        return `₹${crore} crore ${lakh > 0 ? lakh + ' lakh' : ''}`.trim();
      }
    } else if (num >= 100000) {
      const lakh = Math.floor(num / 100000);
      const remaining = num % 100000;
      if (remaining === 0) {
        return `₹${lakh} lakh`;
      } else {
        const thousand = Math.floor(remaining / 1000);
        return `₹${lakh} lakh ${thousand > 0 ? thousand + ' thousand' : ''}`.trim();
      }
    } else if (num >= 1000) {
      const thousand = Math.floor(num / 1000);
      return `₹${thousand} thousand`;
    } else {
      return `₹${num}`;
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-palamuru-blue mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Payments...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Payments</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-palamuru-gold text-white px-4 py-2 rounded-lg hover:bg-palamuru-gold-dark transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display-bold text-gray-900">Payments & Invoicing</h1>
          <p className="text-gray-600 mt-1">Track payments, generate invoices, and manage payment status across all projects.</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-body-semibold text-sm flex items-center space-x-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span>Add Payment</span>
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Amount</p>
              <p className="text-3xl font-bold text-gray-900">₹{totalAmount.toLocaleString()}</p>
              <p className="text-xs font-medium text-gray-500 mt-1">{numberToIndianFormat(totalAmount)}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Amount</p>
              <p className="text-3xl font-bold text-yellow-600">₹{pendingAmount.toLocaleString()}</p>
              <p className="text-xs font-medium text-gray-500 mt-1">{numberToIndianFormat(pendingAmount)}</p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-full">
              <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Overdue Amount</p>
              <p className="text-3xl font-bold text-red-600">₹{overdueAmount.toLocaleString()}</p>
              <p className="text-xs font-medium text-gray-500 mt-1">{numberToIndianFormat(overdueAmount)}</p>
            </div>
            <div className="p-3 bg-red-100 rounded-full">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Paid Amount</p>
              <p className="text-3xl font-bold text-green-600">₹{paidAmount.toLocaleString()}</p>
              <p className="text-xs font-medium text-gray-500 mt-1">{numberToIndianFormat(paidAmount)}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-6 rounded-2xl shadow-soft border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-body-semibold text-gray-700 mb-2">Search Payments</label>
            <input
              type="text"
              placeholder="Search by description, invoice number, or customer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-body-semibold text-gray-700 mb-2">Filter by Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              {statusOptions.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-body-semibold text-gray-700 mb-2">Filter by Project</label>
            <select
              value={filterProject}
              onChange={(e) => setFilterProject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Projects</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterStatus('all');
                setFilterProject('all');
              }}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Payments Table */}
      <div className="bg-white rounded-2xl shadow-soft border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Details</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPayments.map(payment => (
                <tr key={payment.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{payment.description}</div>
                      <div className="text-sm text-gray-500">
                        {payment.invoice_number && `Invoice: ${payment.invoice_number}`}
                      </div>
                      <div className="text-sm text-gray-500">{payment.customer_name}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {payment.projectName || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">₹{payment.amount?.toLocaleString()}</div>
                    <div className="text-xs font-medium text-gray-500 mt-1">{numberToIndianFormat(Number(payment.amount))}</div>
                    <div className="text-sm text-gray-500">{payment.payment_type}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {payment.due_date ? new Date(payment.due_date).toLocaleDateString() : 'N/A'}
                    </div>
                    {payment.due_date && isOverdue(payment.due_date) && (
                      <div className="text-xs text-red-600 font-medium">OVERDUE</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(payment.status)}`}>
                      {payment.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      {payment.status.toLowerCase() === 'paid' ? (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Paid</span>
                      ) : payment.status.toLowerCase() === 'cancelled' ? (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-700">Cancelled</span>
                      ) : (
                        <>
                          <select
                            value={payment.status}
                            onChange={(e) => handleUpdatePaymentStatus(payment.id, e.target.value)}
                            className="text-xs border border-gray-300 rounded px-2 py-1"
                          >
                            {statusOptions.map(status => (
                              <option key={status} value={status}>{status}</option>
                            ))}
                          </select>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filteredPayments.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">💰</div>
          <h3 className="text-lg font-display-semibold text-gray-900 mb-2">No payments found</h3>
          <p className="text-gray-600 mb-4">
            {searchTerm || filterStatus !== 'all' || filterProject !== 'all'
              ? 'Try adjusting your search or filter criteria.'
              : 'Get started by adding your first payment record.'
            }
          </p>
          {!searchTerm && filterStatus === 'all' && filterProject === 'all' && (
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Add First Payment
            </button>
          )}
        </div>
      )}

      {/* Add Payment Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Add New Payment</h2>
            <form onSubmit={handleCreatePayment} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
                  <select
                    value={newPayment.project_id}
                    onChange={(e) => setNewPayment({...newPayment, project_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Select Project</option>
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>{project.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Type</label>
                  <select
                    value={newPayment.payment_type}
                    onChange={(e) => setNewPayment({...newPayment, payment_type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Select Type</option>
                    {paymentTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹)</label>
                  <input
                    type="number"
                    value={newPayment.amount}
                    onChange={(e) => setNewPayment({...newPayment, amount: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                  {newPayment.amount && (
                    <div className="mt-1 text-xs font-medium text-blue-600">
                      {numberToIndianFormat(Number(newPayment.amount))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <input
                    type="date"
                    value={newPayment.due_date}
                    onChange={(e) => setNewPayment({...newPayment, due_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newPayment.description}
                  onChange={(e) => setNewPayment({...newPayment, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Number</label>
                  <input
                    type="text"
                    value={newPayment.invoice_number}
                    onChange={(e) => setNewPayment({...newPayment, invoice_number: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Auto-generated if empty"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={newPayment.status}
                    onChange={(e) => setNewPayment({...newPayment, status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {statusOptions.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  disabled={creatingPayment}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors ${creatingPayment ? 'opacity-60 cursor-not-allowed' : ''}`}
                  disabled={creatingPayment}
                >
                  {creatingPayment ? (
                    <span className="flex items-center"><svg className="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg>Creating...</span>
                  ) : 'Add Payment'}
                </button>
              </div>
              {creatingPayment && (
                <div className="flex items-center justify-center mt-4">
                  <svg className="animate-spin h-6 w-6 text-blue-600 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg>
                  <span className="text-blue-700 font-medium">Creating payment request… Please wait.</span>
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentManagement; 